/**
 * Pre-order bonus gift lines.
 *
 * Earned only on pre-order lines, unless a campaign sets allow_non_preorder,
 * which lets it keep running on regular purchases after release.
 *
 * Keeps the cart's $0 bonus lines in step with what the cart actually earns.
 * One idempotent reconcile(), run on PreProduct's ppJustConverted, on the
 * theme's cart-update, and on load.
 *
 * WHAT THIS TRUSTS
 * Availability arrives as a resolved boolean on each campaign in the
 * <script data-bonus-cart-data> blob, computed server-side by
 * snippets/bonus-availability.liquid. Nothing in this file reads
 * inventory_quantity, inventory_policy, or any other stock figure, and nothing
 * in this file knows where the allocation counter lives. It asks the blob and
 * believes it. That is what lets the counter move to a metafield or to Cin7
 * without touching a line of JS.
 *
 * Date windows and Show Bonuses are applied in Liquid too: a campaign that is
 * out of window or switched off is simply absent from the blob.
 *
 * FAIL SOFT
 * Every failure path logs to the console, leaves the cart otherwise untouched,
 * and lets the page (which simply drops a sold-out bonus) stand. A missing freebie must
 * never surface an error to the customer or stop a pre-order completing.
 */
(function () {
  'use strict';

  // The blob renders in the drawer, on the cart page AND on the product page,
  // so on a product page this script is loaded twice. Register once.
  if (window.__overkillBonusGift) return;
  window.__overkillBonusGift = true;

  var BLOB_SELECTOR = 'script[data-bonus-cart-data]';
  var PROP_CAMPAIGN = '_bonus_campaign';
  var PROP_FOR = '_bonus_for';
  var PROP_VISIBLE = 'Pre-order bonus';
  // The same label for a bonus earned on a regular (non-pre-order) line, which
  // only happens on a campaign with allow_non_preorder. Display only: nothing
  // load-bearing reads either label on a placed order.
  var PROP_VISIBLE_REGULAR = 'Bonus';
  var DEBOUNCE_MS = 200;

  function routes() {
    return (window.FoxTheme && window.FoxTheme.routes) || {};
  }

  function log(message, detail) {
    console.warn('[bonus-gift] ' + message, detail === undefined ? '' : detail);
  }

  /* ---------------------------------------------------------------------
   * Serialised cart calls.
   *
   * Shopify's Ajax cart is not concurrency-safe and PreProduct is mutating
   * the same cart from its own script. Two overlapping writes are the most
   * likely way to end up with a silently wrong cart, so every call this file
   * makes goes through one promise chain, in order, no exceptions.
   *
   * The chain absorbs rejections (same handler for both outcomes) so one
   * failed call can never wedge the queue for the rest of the page's life.
   * ------------------------------------------------------------------- */
  var chain = Promise.resolve();
  function noop() {}
  function enqueue(fn) {
    var run = function () {
      return fn();
    };
    var next = chain.then(run, run);
    chain = next.then(noop, noop);
    return next;
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var error = new Error(data && data.description ? data.description : 'Cart request failed');
          error.status = response.status;
          error.data = data;
          throw error;
        }
        return data;
      });
    });
  }

  function getCart() {
    return fetch(routes().cart_url || '/cart', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    }).then(function (response) {
      return response.json();
    });
  }

  function readBlob() {
    var nodes = document.querySelectorAll(BLOB_SELECTOR);
    if (!nodes.length) return null;

    // The drawer and the product page can both be present. Merge rather than
    // pick: the product page knows the campaign the shopper is about to
    // trigger, the drawer knows the ones already in the cart.
    // An EMPTY campaign map is meaningful, not nothing: it means the cart holds
    // a bonus line whose trigger has gone. Returning null there would skip the
    // orphan sweep and strand the line permanently. Only a page with no blob at
    // all returns null.
    var merged = {};
    var parsedAny = false;
    for (var i = 0; i < nodes.length; i++) {
      var parsed;
      try {
        parsed = JSON.parse(nodes[i].textContent);
      } catch (error) {
        log('could not parse the campaign blob; doing nothing', error);
        continue;
      }
      var byVariant = parsed && parsed.campaignsByVariant;
      if (!byVariant) continue;
      parsedAny = true;
      for (var key in byVariant) {
        if (Object.prototype.hasOwnProperty.call(byVariant, key)) merged[key] = byVariant[key];
      }
    }
    return parsedAny ? merged : null;
  }

  /* ---------------------------------------------------------------------
   * Line classification
   *
   * RETAIL-TWIN GUARD. Some bonus items are also sold as ordinary products:
   * a separate Shopify product, its own SKU, the same barcode - and sometimes
   * literally the same variant. A line only counts as a campaign's bonus when
   * the variant id matches that campaign's bonus variant AND the line carries
   * _bonus_campaign for that campaign. Both, together. Never match on product,
   * title, SKU or barcode.
   *
   * A paid retail line carries no properties, so it is never counted toward a
   * target, never adjusted, and never removed.
   * ------------------------------------------------------------------- */
  function lineProp(line, key) {
    return line && line.properties ? line.properties[key] : undefined;
  }

  function isBonusLineOf(line, handle, def) {
    if (lineProp(line, PROP_CAMPAIGN) !== handle) return false;
    return line.variant_id === def.bonusVariantId;
  }

  /**
   * A bonus line whose campaign is no longer in the blob - the trigger has
   * left the cart, so Liquid stopped emitting the definition and there is no
   * bonus variant id to check against.
   *
   * The property test alone already excludes a paid retail line, but with no
   * definition to check we require more, not less: both our properties present
   * AND an original price of zero. A retail twin fails all three, and a
   * genuinely discounted retail line still has a non-zero original_price.
   */
  function isOrphanBonusLine(line) {
    return (
      typeof lineProp(line, PROP_CAMPAIGN) === 'string' &&
      typeof lineProp(line, PROP_FOR) !== 'undefined' &&
      line.original_price === 0
    );
  }

  function isAnyBonusLine(line) {
    return typeof lineProp(line, PROP_CAMPAIGN) === 'string';
  }

  /* ---------------------------------------------------------------------
   * Planning
   * ------------------------------------------------------------------- */
  function buildPlan(cart, campaignsByVariant) {
    var campaigns = Object.create(null); // handle -> { def, triggers: [...] }
    var i;
    var j;

    for (i = 0; i < cart.items.length; i++) {
      var line = cart.items[i];

      // A bonus line is never itself a trigger.
      if (isAnyBonusLine(line)) continue;

      var defs = campaignsByVariant[String(line.variant_id)];
      if (!defs) continue;

      var isPreorderLine = !!line.selling_plan_allocation;

      for (j = 0; j < defs.length; j++) {
        var def = defs[j];

        // Gate 1: only a genuine pre-order earns a bonus, unless the campaign
        // opts in with allow_non_preorder. PreProduct attaches a selling plan
        // to the pre-order line, so this is a structural check that does not
        // depend on anyone remembering to set an end date. 20 of the 82
        // products carrying a campaign today are no longer pre-orders. The
        // opt-in is per campaign so those 20 stay switched off.
        if (!isPreorderLine && !def.allowNonPreorder) continue;

        if (def.configError || !def.bonusVariantId) {
          log(
            'campaign "' + def.handle + '" is set to physical but has no bonus variant. ' +
              'Nothing will be added. Fix the metaobject entry in the admin.',
            def
          );
          continue;
        }

        var entry = campaigns[def.handle];
        if (!entry) {
          entry = campaigns[def.handle] = { def: def, triggers: [] };
        }
        entry.triggers.push({
          variantId: line.variant_id,
          units: line.quantity * (def.qtyPerUnit || 1),
          preorder: isPreorderLine,
        });
      }
    }

    // Clamp to the per-order cap, greedily in cart order. The cap is a
    // campaign-level limit, so which trigger loses out is arbitrary; the
    // warehouse and the order confirmation read the visible campaign-name
    // property, not _bonus_for.
    for (var handle in campaigns) {
      var c = campaigns[handle];
      var cap = c.def.perOrderCap || 0; // 0 or blank means uncapped, by design
      if (cap <= 0) continue;
      var remaining = cap;
      for (i = 0; i < c.triggers.length; i++) {
        var take = Math.min(c.triggers[i].units, remaining);
        c.triggers[i].units = take;
        remaining -= take;
      }
    }

    return campaigns;
  }

  function planMutations(cart, campaigns) {
    var adds = [];
    var changes = [];
    var handled = Object.create(null);
    var handle;
    var i;

    for (handle in campaigns) {
      var c = campaigns[handle];
      var def = c.def;
      handled[handle] = true;

      // A campaign owns 1..n lines, not one: _bonus_for differs per trigger
      // variant, so Shopify splits them. PS5 + Xbox of one game is two lines
      // of qty 1, never one line of qty 2. Always SUM across matches; never
      // read the first match as "the" bonus line.
      var lines = cart.items.filter(
        (function (boundHandle, boundDef) {
          return function (line) {
            return isBonusLineOf(line, boundHandle, boundDef);
          };
        })(handle, def)
      );

      var wantByTrigger = Object.create(null);
      var preorderByTrigger = Object.create(null);
      for (i = 0; i < c.triggers.length; i++) {
        var t = c.triggers[i];
        wantByTrigger[String(t.variantId)] = (wantByTrigger[String(t.variantId)] || 0) + t.units;
        if (t.preorder) preorderByTrigger[String(t.variantId)] = true;
      }

      var haveByTrigger = Object.create(null);
      for (i = 0; i < lines.length; i++) {
        var seenFor = String(lineProp(lines[i], PROP_FOR));
        haveByTrigger[seenFor] = (haveByTrigger[seenFor] || 0) + lines[i].quantity;
      }

      // Reduce or remove first, including lines whose trigger has left the
      // cart. This runs even when the campaign is sold out: the sold-out rule
      // stops us ADDING, it does not entitle a shopper to keep bonuses for a
      // trigger they removed.
      for (i = 0; i < lines.length; i++) {
        var soldLine = lines[i];
        var forKey = String(lineProp(soldLine, PROP_FOR));
        var want = wantByTrigger[forKey] || 0;
        var have = haveByTrigger[forKey] || 0;
        if (have > want) {
          var surplus = have - want;
          var newQuantity = Math.max(0, soldLine.quantity - surplus);
          haveByTrigger[forKey] = have - (soldLine.quantity - newQuantity);
          changes.push({ key: soldLine.key, quantity: newQuantity });
        }
      }

      // Then top up. Never when the campaign is sold out - someone who
      // already claimed one keeps it, but nobody new gets one.
      if (!def.available) continue;

      for (var triggerId in wantByTrigger) {
        var shortfall = wantByTrigger[triggerId] - (haveByTrigger[triggerId] || 0);
        if (shortfall <= 0) continue;
        var properties = {};
        properties[PROP_CAMPAIGN] = handle;
        properties[PROP_FOR] = triggerId;
        properties[preorderByTrigger[triggerId] ? PROP_VISIBLE : PROP_VISIBLE_REGULAR] = def.name;
        adds.push({ id: def.bonusVariantId, quantity: shortfall, properties: properties });
      }
    }

    // Orphans: a bonus line whose campaign is not in the blob at all. Its
    // trigger has gone, so it is no longer earned.
    for (i = 0; i < cart.items.length; i++) {
      var orphan = cart.items[i];
      if (!isOrphanBonusLine(orphan)) continue;
      if (handled[lineProp(orphan, PROP_CAMPAIGN)]) continue;
      changes.push({ key: orphan.key, quantity: 0 });
    }

    return { adds: adds, changes: changes };
  }

  /* ---------------------------------------------------------------------
   * Mutating
   * ------------------------------------------------------------------- */

  /**
   * A batched /cart/add.js is ATOMIC: if any one item is unavailable the whole
   * call 422s and nothing is added. On a product with two bonuses, one of them
   * being sold out would therefore silently block the other. So: try the batch
   * (one round trip in the common case), and on failure retry the items one at
   * a time and take what we can get.
   */
  function addItems(items, sections) {
    var url = routes().cart_add_url || '/cart/add';
    return postJSON(url, { items: items, sections: sections }).catch(function (error) {
      if (items.length < 2) {
        log('could not add a bonus line; leaving the cart alone', error);
        return null;
      }
      log('batched bonus add was rejected as a whole; retrying individually', error);
      var lastOk = null;
      return items
        .reduce(function (previous, item) {
          return previous.then(function () {
            return postJSON(url, { items: [item], sections: sections })
              .then(function (data) {
                lastOk = data;
              })
              .catch(function (itemError) {
                log('bonus "' + item.properties[PROP_CAMPAIGN] + '" could not be added', itemError);
              });
          });
        }, Promise.resolve())
        .then(function () {
          return lastOk;
        });
    });
  }

  function applyChanges(changes, sections) {
    var url = routes().cart_change_url || '/cart/change';
    var lastOk = null;
    return changes
      .reduce(function (previous, change) {
        return previous.then(function () {
          return postJSON(url, { id: change.key, quantity: change.quantity, sections: sections })
            .then(function (data) {
              lastOk = data;
            })
            .catch(function (error) {
              log('could not adjust a bonus line; leaving it alone', error);
            });
        });
      }, Promise.resolve())
      .then(function () {
        return lastOk;
      });
  }

  /**
   * Which sections the cart wants re-rendered. The theme collects these by
   * dispatching an event that each interested element pushes its own id into:
   * <main-cart> pushes 'main-cart', <cart-drawer> pushes 'cart-drawer'. We ask
   * the same way, so we get whichever exists without knowing which it is.
   *
   * This store runs settings.cart_type = 'page', so sections/cart-drawer.liquid
   * never renders and the drawer element does not exist. The earlier version of
   * this file dispatched 'cart:refresh', which ONLY CartDrawer listens for -
   * so on a cart-page store nothing re-rendered and the bonus line appeared
   * only after a manual reload. Do not go back to that: bundle sections and
   * publish cartUpdate, which CartItems.onCartUpdate handles for the drawer AND
   * for #MainCart.
   */
  function collectSections() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  /**
   * Hand the freshly mutated cart to the theme, with the re-rendered section
   * HTML the mutation returned. CartItems.onCartUpdate swaps #MainCart (or the
   * drawer's three regions) from it, and publishes cart:updated afterwards.
   */
  /**
   * The item count the cart HTML was drawn with, from the blob, or null when no
   * blob carries one (the product page's does not).
   */
  function readRenderedItemCount() {
    var nodes = document.querySelectorAll(BLOB_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      try {
        var parsed = JSON.parse(nodes[i].textContent);
        if (parsed && typeof parsed.renderedItemCount === 'number') return parsed.renderedItemCount;
      } catch (error) {
        /* readBlob already logs an unparseable blob */
      }
    }
    return null;
  }

  /**
   * Redraw the cart from the server without changing it. Needed when the page
   * was rendered before an add landed - see snippets/bonus-cart-data.liquid.
   * GET ?sections= returns the same { id: html } map a cart mutation does, so
   * the theme's own onCartUpdate does the swap. No Accept: application/json
   * here: on /cart that header asks for the cart itself (see getCart), not the
   * sections.
   */
  function redrawCart(cart) {
    var ids = collectSections();
    if (!ids.length) return Promise.resolve(null);
    var url = (routes().cart_url || '/cart') + '?sections=' + ids.map(encodeURIComponent).join(',');
    return fetch(url)
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (sections) {
        return sections ? publishCartUpdate(sections, cart) : null;
      });
  }

  function publishCartUpdate(sections, knownCart) {
    return Promise.resolve(knownCart || getCart()).then(function (cart) {
      if (!cart) return null;
      // onCartUpdate bails out early when sections is undefined, which would
      // leave the DOM stale even though the cart itself changed.
      if (sections) cart.sections = sections;
      var pubsub = window.FoxTheme && window.FoxTheme.pubsub;
      if (pubsub) pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
      return 1;
    });
  }

  /* ---------------------------------------------------------------------
   * Reconcile
   * ------------------------------------------------------------------- */
  var reconciling = false;
  // Only the first run checks the HTML against the cart: after that, every
  // redraw comes from a mutation response, so the page is never older than it.
  var firstRun = true;

  /**
   * @param {object} [knownCart] A cart we already have, so we can skip a round
   *   trip. The theme's cartUpdate payload carries the full cart, and
   *   /cart/change.js returns it too - re-fetching either is ~0.3s of nothing.
   *   Only ever a cart the server just gave us; never a snapshot from the page,
   *   which would be stale the moment anything else touched the cart.
   */
  function reconcile(knownCart) {
    return enqueue(function () {
      // Guard against a reconcile triggered by our own mutation: the cart
      // event we cause fires while we are still inside this call.
      if (reconciling) return Promise.resolve();

      var campaignsByVariant = readBlob();
      if (!campaignsByVariant) return Promise.resolve();

      var checkStale = firstRun;
      firstRun = false;
      var renderedItemCount = checkStale ? readRenderedItemCount() : null;

      reconciling = true;
      return Promise.resolve(knownCart && knownCart.items ? knownCart : getCart())
        .then(function (cart) {
          if (!cart || !cart.items) return null;

          var campaigns = buildPlan(cart, campaignsByVariant);
          var plan = planMutations(cart, campaigns);

          // Idempotent: no difference between target and actual means no
          // request, no re-render, and therefore no event to loop on - UNLESS
          // this is the first run and the page was drawn with a different cart
          // than the server now has (a pre-order bonus that landed after /cart
          // rendered). Then redraw once, without touching the cart.
          if (!plan.adds.length && !plan.changes.length) {
            if (renderedItemCount !== null && cart.item_count !== renderedItemCount) return redrawCart(cart);
            return null;
          }

          var sections = collectSections();

          // Removals first, so a cap or a reduced trigger frees room before
          // anything is added.
          return applyChanges(plan.changes, sections)
            .then(function (changeResult) {
              if (!plan.adds.length) return changeResult;
              return addItems(plan.adds, sections).then(function (addResult) {
                return addResult || changeResult;
              });
            })
            .then(function (lastResponse) {
              if (!lastResponse) return null;
              // /cart/change.js returns the whole cart; /cart/add.js returns
              // only the lines it added. So a removal-only pass can publish
              // straight away, and only an add needs the extra fetch.
              var full = lastResponse.items && lastResponse.item_count !== undefined ? lastResponse : null;
              return publishCartUpdate(lastResponse.sections, full);
            });
        })
        .catch(function (error) {
          log('reconcile failed; the cart is unchanged', error);
          return null;
        })
        .then(function (result) {
          reconciling = false;
          return result;
        });
    });
  }


  /* ---------------------------------------------------------------------
   * Pre-add, on the product page
   *
   * On a cart_type='page' store the buy button form-POSTs and the browser
   * navigates to /cart. If we wait until that page loads to add the bonus, the
   * cart renders complete-looking and then visibly gains a line about 1.7s
   * later - of which ~0.9s is Shopify's own /cart/add.js and cannot be tuned
   * away. The only way to remove the delay is to stop adding the line after
   * the page that displays it.
   *
   * So when PreProduct tells us it has just converted, we add the bonus
   * immediately, with keepalive:true so the request survives the navigation.
   * /cart then renders the bonus line server-side, and reconcile() finds
   * nothing to do.
   *
   * This is deliberately SPECULATIVE: we have not seen the cart, so we are
   * trusting that PreProduct added what it said it did, at the quantity shown
   * on the page. Everything it can get wrong, reconcile() corrects on the cart
   * page - an over-add is reduced, an under-add is topped up, and a bonus
   * whose trigger never materialised is swept as an orphan. Being wrong costs
   * a correction; being slow cost every shopper 1.7s.
   * ------------------------------------------------------------------- */
  function selectedVariantId() {
    // The variant picker publishes the selected variant as JSON - the same
    // source assets/product-info.js reads. The form's own id input is the
    // fallback, and note it is deliberately DISABLED for pre-orders
    // (snippets/buy-buttons.liquid), which does not stop us reading .value.
    var node = document.querySelector('variant-selects [data-selected-variant]');
    if (node) {
      try {
        var parsed = JSON.parse(node.textContent);
        if (parsed && parsed.id) return String(parsed.id);
      } catch (error) {
        /* fall through to the form input */
      }
    }
    var input = document.querySelector('form[data-type="add-to-cart-form"] [name="id"]');
    return input && input.value ? String(input.value) : null;
  }

  function triggerQuantity() {
    var input = document.querySelector('form[data-type="add-to-cart-form"] [name="quantity"]');
    var parsed = input ? parseInt(input.value, 10) : NaN;
    return parsed > 0 ? parsed : 1;
  }

  function preAddOnConversion() {
    var campaignsByVariant = readBlob();
    if (!campaignsByVariant) return;

    var variantId = selectedVariantId();
    if (!variantId) {
      // Cannot tell what was just bought - fall back to reconciling properly.
      scheduleReconcile();
      return;
    }

    var defs = campaignsByVariant[variantId];
    if (!defs || !defs.length) return;

    var quantity = triggerQuantity();
    var items = [];
    for (var i = 0; i < defs.length; i++) {
      var def = defs[i];
      if (def.configError || !def.bonusVariantId) {
        log('campaign "' + def.handle + '" is physical but has no bonus variant; skipping', def);
        continue;
      }
      if (!def.available) continue;

      var units = quantity * (def.qtyPerUnit || 1);
      if (def.perOrderCap > 0) units = Math.min(units, def.perOrderCap);
      if (units <= 0) continue;

      var properties = {};
      properties[PROP_CAMPAIGN] = def.handle;
      properties[PROP_FOR] = variantId;
      properties[PROP_VISIBLE] = def.name;
      items.push({ id: def.bonusVariantId, quantity: units, properties: properties });
    }
    if (!items.length) return;

    enqueue(function () {
      var url = routes().cart_add_url || '/cart/add';
      var sections = collectSections();
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items, sections: sections }),
        // Survives the navigation the buy button is about to trigger. Without
        // this the browser cancels the request on unload and the bonus is only
        // added later, by the cart page, which is the delay we are removing.
        keepalive: true,
      })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (data) {
          // Only reached when the page did NOT navigate - a drawer store, or a
          // conversion that leaves you where you are. Repaint so the line shows.
          if (data && data.sections) return publishCartUpdate(data.sections);
          return null;
        })
        .catch(function (error) {
          log('pre-add did not complete; the cart page will add the bonus instead', error);
          return null;
        });
    });
  }

  var debounceTimer = null;
  var pendingCart = null;
  function scheduleReconcile(payload) {
    // The theme publishes cartUpdate with the full cart attached. Carrying it
    // through saves re-asking the server for something we were just handed.
    if (payload && payload.cart && payload.cart.items) pendingCart = payload.cart;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      var cart = pendingCart;
      pendingCart = null;
      reconcile(cart);
    }, DEBOUNCE_MS);
  }

  /* ---------------------------------------------------------------------
   * Wiring
   * ------------------------------------------------------------------- */

  // PreProduct fires this after it has added the pre-order line. Which target
  // it dispatches on is its business and not ours to depend on, so listen on
  // both - and guard against it arriving twice, because this handler mutates
  // the cart rather than reconciling it and a double delivery would double-add.
  var converted = false;
  function onConverted() {
    if (converted) return;
    converted = true;
    preAddOnConversion();
  }
  document.addEventListener('ppJustConverted', onConverted);
  window.addEventListener('ppJustConverted', onConverted);

  // The theme's cart event. Payload is passed directly, not as an Event.
  if (window.FoxTheme && window.FoxTheme.pubsub) {
    window.FoxTheme.pubsub.subscribe(
      window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      scheduleReconcile
    );
  }
  document.addEventListener('cart:updated', function (event) {
    scheduleReconcile(event && event.detail);
  });

  // First run is not debounced. The shopper is already looking at the cart, and
  // on a cart_type='page' store the bonus line can only appear after this runs,
  // so every 200ms here is 200ms of visibly incomplete cart.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();
